import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Pagination,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getPmjayList } from 'api/pmjay';
import PmjayFormModal from "./PmjayFormModal";
import PmjayImportModal from "./PmjayImportModal";

const PMJAY_SPECIALTIES = [
  "Cardiology",
  "Orthopedics",
  "Neurology",
  "Oncology",
  "Urology",
  "Gynecology",
  "Pediatrics",
  "General Surgery",
  "ENT",
  "Ophthalmology",
  "Nephrology",
  "Gastroenterology",
  "Pulmonology",
  "Endocrinology",
  "Dermatology",

  "Burns Management",
  "Emergency Room Packages",
  "High end Diagnostics",
  "High end Medicine",
  "High end procedures",
  "Infectious Diseases",
  "Interventional Radiology",
  "CTVS",
  "General Medicine",
  "Pediatric Medical Management",
  "Pain specialists",
  "Interventional Neuroradiology",
  "General medicine / high end drugs",
  "Medical Rehabilitation",

  "Other",
];

const PmjayList = () => {
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formModal, setFormModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const perPage = 10;

  const fetchList = async (pg = 1) => {
    setIsLoading(true);
    try {
      const res = await getPmjayList({
        page: pg,
        limit: perPage,
        ...(search && { search }),
        ...(specialty && { specialty }),
        ...(status && { status }),
      });
      setList(res?.data?.data?.pmjayList || []);
      setTotalCount(res?.data?.data?.totalCount || 0);
    } catch {
      toast.error("Error while fetching PMJAY packages!");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchList(page);
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    fetchList(1);
  };

  const handleReset = () => {
    setSearch("");
    setSpecialty("");
    setStatus("");
    setPage(1);
    setTimeout(() => fetchList(1), 100);
  };

  const handleModalClose = (type) => {
    setFormModal(false);
    setSelectedData({});
    if (type !== "close") fetchList(page);
  };

  const handleImportClose = () => {
    setImportModal(false);
    fetchList(page);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" fontWeight="bold">
          PMJAY Package Master
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => setImportModal(true)}
            sx={{ borderColor: "#194AF5", color: "#194AF5", boxShadow: "none" }}
          >
            Bulk Import
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSelectedData({});
              setFormModal(true);
            }}
            sx={{ boxShadow: "none" }}
          >
            + Add Package
          </Button>
        </Box>
      </Grid>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Search by Name / Code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Specialty</InputLabel>
            <Select
              value={specialty}
              label="Specialty"
              onChange={(e) => setSpecialty(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {PMJAY_SPECIALTIES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={1.5}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSearch}
            sx={{ boxShadow: "none", height: "40px" }}
          >
            Search
          </Button>
        </Grid>
        <Grid item xs={12} sm={1.5}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleReset}
            sx={{ borderColor: "#194AF5", color: "#194AF5", height: "40px" }}
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer
        sx={{ borderRadius: "12px", border: "1px solid #E0E0E0" }}
      >
        <Table>
          <TableHead sx={{ background: "#F0F4FF" }}>
            <TableRow>
              {[
                "S.No",
                "Package Code",
                "Package Name",
                "Specialty",
                "Specialty Code",
                "Tier 3",
                "Tier 2",
                "Tier 1",
                "Implant Cost",
                "Status",
                "Action",
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{ fontWeight: "bold", fontSize: "13px" }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={11}
                  align="center"
                  sx={{ py: 5, color: "text.secondary" }}
                >
                  No PMJAY Package Found!
                </TableCell>
              </TableRow>
            ) : (
              list.map((item, idx) => (
                <TableRow key={item._id} hover>
                  <TableCell>{(page - 1) * perPage + idx + 1}</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#194AF5" }}>
                    {item.packageCode}
                  </TableCell>
                  <TableCell>{item.packageName}</TableCell>
                  <TableCell>{item.specialty}</TableCell>
                  <TableCell>{item.specialtyCode || "-"}</TableCell>
                  <TableCell>
                    {item.tier3Amount != null ? `₹${item.tier3Amount}` : "-"}
                  </TableCell>
                  <TableCell>
                    {item.tier2Amount != null ? `₹${item.tier2Amount}` : "-"}
                  </TableCell>
                  <TableCell>
                    {item.tier1Amount != null ? `₹${item.tier1Amount}` : "-"}
                  </TableCell>
                  <TableCell>
                    {item.implantCost != null ? `₹${item.implantCost}` : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        bgcolor:
                          item.status === "ACTIVE" ? "#E6F9EE" : "#FFE6E6",
                        color: item.status === "ACTIVE" ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSelectedData(item);
                        setFormModal(true);
                      }}
                      sx={{
                        borderColor: "#194AF5",
                        color: "#194AF5",
                        fontSize: "12px",
                        minWidth: "60px",
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalCount > perPage && (
        <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
          <Pagination
            count={Math.ceil(totalCount / perPage)}
            page={page}
            onChange={(_, val) => setPage(val)}
            color="primary"
          />
        </Box>
      )}

      {/* Total Count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        Total: {totalCount} packages
      </Typography>

      {/* Modals */}
      {formModal && (
        <PmjayFormModal
          open={formModal}
          onClose={handleModalClose}
          data={selectedData}
        />
      )}
      {importModal && (
        <PmjayImportModal open={importModal} onClose={handleImportClose} />
      )}
    </Box>
  );
};

export default PmjayList;
